import React, { useState } from 'react';
import { LANDING_PATH } from '../../msg/inviteLink';
// SEC-5: мёртвый `import axios from 'axios'` убран — в файле он не использовался
// (запросы идут через axiosInstance), но тянул ESM-пакет в jest-окружение и ронял
// разбор тест-файла ещё до первого рендера.
import { useNavigate } from 'react-router-dom';
import Input from '../../components/UI/Input/Input';
import { useTheme } from '../../hooks/useTheme';
import './auth.css';
import axiosInstance from '../../utils/router/axiosInstance';
import { logger } from '../../utils/logger';
import { useAuth } from '../../context/AuthContext';

const Auth = () => {
  // Зовём ради побочного эффекта: хук ставит data-theme на <html> из
  // localStorage. Без него страница входа осталась бы на палитре :root, даже
  // если владелец держит кабинет в тёмной теме (а он держит).
  useTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { refreshMe } = useAuth();

  const handleLogin = async () => {
    logger.debug('попытка входа', { username });  // пароль НЕ логируем
    try {
      const response = await axiosInstance.post('website/auth', {
        username,
        password,
      });
      if (response.status === 200) {
        const tokens = response.data;
        const access_token = tokens[0].access_token;
        const refresh_token = tokens[1].refresh_token;
        localStorage.setItem('access_token', access_token);
        document.cookie = `refresh_token=${refresh_token}; path=/`;

        logger.debug('Успешная аутентификация');
        // WEB-2: дождаться профиля (роль) ДО навигации — иначе RequireRole
        // увидит устаревшее «loaded, роли нет» и отфутболит админа в профиль
        await refreshMe();
        navigate('/settings/generate-code'); // Перенаправление на /settings/generate-code
      } else {
        logger.debug('Ошибка аутентификации');
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setError('Неверный логин или пароль');
      } else if (error.response && error.response.status === 429) {
        // SEC-5: edge-nginx ограничивает частоту входов (10 попыток в минуту с
        // адреса, всплеск до 6 подряд). Без этой ветки владелец увидел бы
        // «Ошибка аутентификации: 429» и решил, что сломался сервер, — а пароль
        // при этом мог быть верным.
        setError('Слишком много попыток входа. Подождите минуту и попробуйте снова');
      } else {
        setError(`Ошибка аутентификации: ${error.response ? error.response.status : 'неизвестно'}`);
      }
    }
  };

  // Enter в поле — тот же вход. Форма настоящая (<form onSubmit>), а не div с
  // кнопкой: иначе менеджеры паролей не предлагают сохранить пару, а Enter
  // в поле пароля не делает ничего.
  const handleSubmit = (event) => {
    event.preventDefault();
    handleLogin();
  };

  return (
    <div className="authPage">
      <section className="authBrand">
        <svg className="authBrandArt" viewBox="0 0 720 720" fill="none" aria-hidden="true" focusable="false">
          <circle cx="360" cy="520" r="90" stroke="#ffffff" strokeWidth="2" />
          <circle cx="360" cy="520" r="170" stroke="#ffffff" strokeWidth="2" />
          <circle cx="360" cy="520" r="250" stroke="#ffffff" strokeWidth="2" />
          <circle cx="360" cy="520" r="330" stroke="#ffffff" strokeWidth="2" />
        </svg>

        <div className="authBrandRow">
          <svg className="authLogo" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
            <path d="M4.5 9.5a10.5 10.5 0 0 1 15 0" />
            <path d="M7.5 13a6.4 6.4 0 0 1 9 0" />
            <path d="M10.5 16.5a2.2 2.2 0 0 1 3 0" />
            <circle cx="12" cy="19.5" r="1" fill="#ffffff" stroke="none" />
          </svg>
          <span className="authWordmark">wificontrol</span>
        </div>

        <div className="authPitch">
          <h1>Где сейчас люди на объекте</h1>
          <p>Позиционирование по Wi-Fi и BLE в реальном времени: план этажа, статус смены, история перемещений.</p>
        </div>

        <div className="authStats">
          <div className="authStat">
            <span className="authStatValue">2,6 м</span>
            <span className="authStatLabel">медианная точность</span>
          </div>
          <div className="authStat">
            <span className="authStatValue">5 с</span>
            <span className="authStatLabel">обновление на карте</span>
          </div>
        </div>
      </section>

      <main className="authFormSide">
        <form className="authForm" onSubmit={handleSubmit}>
          <div className="authFormHead">
            <h2>Авторизация</h2>
            <p>Вход для владельца объекта и администратора.</p>
          </div>

          {/* 🔴 WEB-29: дверь для участника. Сюда приводит ярлык установленного
              приложения, когда сессии в нём ещё нет, — а на iOS её там не будет
              никогда, пока человек не войдёт ВНУТРИ приложения: у него своё
              хранилище, отдельное от Safari. Без этой строки коллега с айфоном
              упирается в форму «Авторизация», где у него нет ни логина, ни
              пароля, и звонит владельцу.
              🔴 WEB-30: строка стоит ДО полей. Под формой она начиналась на
              854 px — ниже первого экрана и iPhone 13, и iPhone SE: дверь,
              ради которой её делали, видна была только прокруткой. */}
          <p className="authHint authInvited">
            Вас пригласили в чат?
            {' '}
            <a href={LANDING_PATH} data-testid="auth-invited">
              Открыть чат по приглашению
            </a>
          </p>

          <div className="authCard">
            {error && (
              <div className="authError" role="alert">
                <svg className="authErrorIcon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true" focusable="false">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7.5v5" />
                  <path d="M12 16.2v.3" />
                </svg>
                <span>{error}</span>
              </div>
            )}

            <div className="authField">
              <label htmlFor="login">Имя пользователя</label>
              <Input
                id={'login'}
                className="authInput"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>

            <div className="authField">
              <label htmlFor="password">Пароль</label>
              <div className="authPasswordRow">
                <Input
                  id={'password'}
                  className="authInput authInputWithButton"
                  type={passwordVisible ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="authReveal"
                  aria-label={passwordVisible ? 'Скрыть пароль' : 'Показать пароль'}
                  aria-pressed={passwordVisible}
                  onClick={() => setPasswordVisible((shown) => !shown)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
                    <circle cx="12" cy="12" r="3" />
                    {passwordVisible && <path d="m4 20 16-16" />}
                  </svg>
                </button>
              </div>
            </div>

            <button type="submit" className="authSubmit">Вход</button>
          </div>

          <p className="authHint">Забыли пароль — обратитесь к администратору объекта.</p>
        </form>
      </main>
    </div>
  );
};

export default Auth;
